import { create } from 'zustand';
import type { HingeDef } from '@/render/hinges';

/**
 * Live registry of hinges in the currently-built house.
 *
 * Lifecycle:
 *  1. `buildHouse` populates `defs` (via App.tsx after each rebuild).
 *  2. UI sliders write into `values` (id → 0..1).
 *  3. App.tsx subscribes to value changes and applies them to the live
 *     Three.js scene via `viewport.setHingeAngle`.
 *
 * On a fresh build the previous values are dropped — IDs are house-local
 * and rebuilds replace the entire set.
 */
export interface HingesState {
  defs: HingeDef[];
  values: Record<string, number>;
  /** Per-id swing-direction inversion. true = swing the opposite way at
   *  the same hinge. Limits stay the same magnitude. */
  inverted: Record<string, boolean>;
  setDefs(defs: HingeDef[]): void;
  setValue(id: string, value: number): void;
  setAll(value: number): void;
  toggleInverted(id: string): void;
  reset(): void;
}

export const useHingesStore = create<HingesState>((set, get) => ({
  defs: [],
  values: {},
  inverted: {},
  setDefs(defs) {
    // Preserve any value whose id still exists; default everything else to 0
    // (closed). This means re-running the same seed leaves your slider
    // positions intact, which is the least surprising thing.
    const prev = get().values;
    const prevInverted = get().inverted;
    const next: Record<string, number> = {};
    const nextInverted: Record<string, boolean> = {};
    for (const d of defs) {
      const fallback = typeof d.defaultT === 'number' ? Math.max(0, Math.min(1, d.defaultT)) : 0;
      next[d.id] = prev[d.id] ?? fallback;
      nextInverted[d.id] = prevInverted[d.id] ?? false;
    }
    set({ defs, values: next, inverted: nextInverted });
  },
  setValue(id, value) {
    const v = Math.max(0, Math.min(1, value));
    set({ values: { ...get().values, [id]: v } });
  },
  setAll(value) {
    const v = Math.max(0, Math.min(1, value));
    const next: Record<string, number> = {};
    for (const d of get().defs) next[d.id] = v;
    set({ values: next });
  },
  toggleInverted(id) {
    const cur = get().inverted[id] ?? false;
    set({ inverted: { ...get().inverted, [id]: !cur } });
  },
  reset() {
    set({ defs: [], values: {}, inverted: {} });
  },
}));
